import { fg } from '@atlassian/jira-feature-gating';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type { SelectOption, SelectValue } from '../../common/types.tsx';
import { isSingleValueOption } from '../../common/utils/is-single-value-option.tsx';
import { SELECTED_COLUMNS_ID } from './constants.tsx';
import messages from './messages.tsx';

/**
 * Adds option to the list of options if its value isn't currently in the Set of keys
 */
export const addUniqueOptions = (
	keys: Set<string>,
	options: SelectOption[],
	option: SelectOption,
) => {
	if (isSingleValueOption(option) && !keys.has(option.value)) {
		keys.add(option.value);
		options.push(option);
	}

	// If the option is a group, we either append to an existing group or add a new group
	if (option.optionType === 'group') {
		const foundDuplicateGroup = fg('jira_inline_field_config_gate')
			? options.some(
					(o) => ('id' in o && o.id === option.id) || ('label' in o && o.label === option.label),
				)
			: options.some((o) => 'label' in o && o.label === option.label);

		if (foundDuplicateGroup) {
			option.options.forEach((groupVal) => {
				const existingGroup = options.find((o) => 'label' in o && o.label === option.label);
				if (existingGroup && existingGroup.optionType === 'group') {
					const newOptions: SelectOption[] = [...existingGroup?.options, groupVal];
					existingGroup.options = newOptions;
				}
			});
		} else {
			if (fg('jira_inline_field_config_gate')) {
				option.options.forEach((groupVal) => {
					if (isSingleValueOption(groupVal)) {
						keys.add(groupVal.value);
					}
				});
			}
			options.push(option);
		}
	}
};

export const generateOptions = (
	selected: SelectValue,
	data: SelectValue,
	formatMessage: FormatMessage,
	previousData?: SelectValue | null,
	addSelectedFirst = true,
): SelectValue => {
	// Handle currently selected
	const optionKeys = new Set<string>();
	const options: SelectOption[] = [];
	if (addSelectedFirst) {
		if (fg('jira_inline_field_config_gate')) {
			addUniqueOptions(optionKeys, options, {
				id: SELECTED_COLUMNS_ID,
				optionType: 'group',
				options: selected,
				label: formatMessage(messages.selectedLabel),
			});
		} else {
			// add currently selected options
			selected &&
				selected.forEach((val) => {
					addUniqueOptions(optionKeys, options, val);
				});
		}
	}

	if (previousData) {
		// add previous loaded options
		previousData.forEach((val) => {
			addUniqueOptions(optionKeys, options, val);
		});
	}

	data &&
		data.forEach((val) => {
			// Add group options
			if (val && val.optionType === 'group') {
				const groupOptions: SelectOption[] = [];
				val.options.forEach((groupVal) => {
					// removing values from the group that are already in the options list (e.g. selected values, values from previousData)
					addUniqueOptions(optionKeys, groupOptions, groupVal);
				});

				if (groupOptions.length > 0) {
					addUniqueOptions(optionKeys, options, {
						...val,
						options: groupOptions,
						optionType: 'group',
					});
				}
				return;
			}

			// add individual options
			addUniqueOptions(optionKeys, options, val);
		});

	return options;
};

export const getValueFromOptions = (
	loadedOptionsWithFullList: SelectValue,
	value: SelectValue,
): SelectValue => {
	if (loadedOptionsWithFullList.length === 0) {
		return value;
	}
	const loadedOptionsWithFullListFlat = loadedOptionsWithFullList.flatMap((option) =>
		option.optionType === 'group' ? option.options : option,
	);
	return value.map(
		(selectedValue: SelectOption) =>
			loadedOptionsWithFullListFlat.find(
				(option: SelectOption) =>
					isSingleValueOption(option) &&
					isSingleValueOption(selectedValue) &&
					selectedValue.value === option.value,
			) || selectedValue,
	);
};
